/**
 * design_handoff_auth §1/§4 — forgot password: form -> link sent, with a
 * resend cooldown in neutral disabled styling (not the old teal).
 * Created by piotr.pozniak@thebeaverhead.com on 20/08/2018.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import isEmail from "validator/lib/isEmail";
import { Link } from "react-router-dom";
import AuthLayout from "./AuthPages/AuthLayout";
import WhatsNewPane from "./AuthPages/WhatsNewPane";
import AuthStateCard from "./AuthPages/AuthStateCard";
import AuthAlert from "./AuthPages/AuthAlert";
import { Input, Button, Icon } from "../theme/untitled";
import { useUserStore } from "../../../hooks/redux/user";
import { useAppStore } from "../../../hooks/redux/app";
import { formatError } from "../../../helpers/errors";
import styles from "./AuthPages/authForm.module.scss";

const RESEND_COOLDOWN_SECONDS = 60;

const formatCountdown = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, "0")}`;
};

const PasswordReminderPage = (props) => {
  const [email, setEmail] = useState("");
  const [emailError, setEmailError] = useState(null);
  const [cooldown, setCooldown] = useState(0);

  const { clearErrors } = useAppStore();
  const { user, remindPassword } = useUserStore();
  const emailRef = useRef();

  const isSignedIn = Boolean(user.model && user.model.uuid);

  // The backend always reports success here even for an unknown email (so
  // it never leaks whether an address has an account) — any remindPasswordError
  // that does come through is a genuine backend failure, not a user mistake.
  const genericErrorMessage = useMemo(() => {
    if (!user.remindPasswordError) {
      return null;
    }
    return formatError(user.remindPasswordError, "Could not send the reset link. Please try again.").join(" ");
  }, [user.remindPasswordError]);

  useEffect(() => {
    return clearErrors;
  }, []);

  useEffect(() => {
    if (!user.remindPasswordSuccess) {
      return;
    }
    setCooldown(RESEND_COOLDOWN_SECONDS);
  }, [user.remindPasswordSuccess]);

  useEffect(() => {
    if (cooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      clearErrors();

      if (!isEmail(email)) {
        setEmailError("Please provide a valid email address.");
        return;
      }
      setEmailError(null);
      await remindPassword(email);
    },
    [email, clearErrors, remindPassword]
  );

  const onStartAgain = useCallback(() => {
    clearErrors();
    setCooldown(0);
  }, [clearErrors]);

  const isLoading = user.remindPassword;

  const title = isSignedIn ? "Change password" : "Reset your password";
  const backButtonLabel = isSignedIn ? "Back to settings" : "Back to log in";
  const backButtonUrl = isSignedIn ? "/settings" : "/login";

  if (user.remindPasswordSuccess) {
    return (
      <AuthLayout rightPane={<WhatsNewPane />}>
        <AuthStateCard
          tone="success"
          icon="CheckCircle"
          title="Check your inbox"
          actions={
            <Button
              hierarchy="secondaryGray"
              iconLeading={<Icon name="RefreshCw02" size={16} />}
              disabled={cooldown > 0}
            >
              {cooldown > 0 ? `Resend in ${formatCountdown(cooldown)}` : "Resend link"}
            </Button>
          }
        >
          <p>
            We sent a reset link to <b>{email}</b>. It expires in 60 minutes.
          </p>
          <p >
            Wrong address?{" "}
            <button type="button" className={styles.linkButton} onClick={onStartAgain}>
              Start again
            </button>
            .
          </p>
        </AuthStateCard>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout rightPane={<WhatsNewPane />}>
      <form action="/" noValidate>
        <h1 className={styles.title}>{title}</h1>
        <p className={styles.sub}>We'll email you a link to set a new one.</p>

        <div className={styles.fields}>
          <Input
            ref={emailRef}
            label="Email"
            type="email"
            value={email}
            autoFocus
            onChange={(e) => setEmail(e.target.value)}
            disabled={isLoading}
            error={emailError}
          />

          {genericErrorMessage && <AuthAlert variant="error">{genericErrorMessage}</AuthAlert>}

          <Button type="submit" fullWidth loading={isLoading} onClick={onSubmit}>
            Send reset link
          </Button>
        </div>

        <p className={styles.footLink}>
          <Link to={backButtonUrl}>{backButtonLabel}</Link>
        </p>
      </form>
    </AuthLayout>
  );
};

export default PasswordReminderPage;
